import { PendingFileDeletion, RecoveredFile, StoredFile } from '@core/models';

import { BusinessModelFileDto } from './business-model.dto';
import { PendingFileDeletionDto, RecoveredFileDto } from './file-deletions.dto';

/**
 * The one translation for the file-deletion queue.
 *
 * Almost all defaulting rather than renaming. Two fields deserve a note:
 * `deletedBy` / `rescuedBy` stay NULLABLE all the way through — the contract is
 * explicit that an administrator with no persona record is ordinary, and
 * flattening that to an empty name here would invent a person. And
 * `daysRemaining` is passed through untouched: it is server-computed against a
 * configurable window, so recomputing it from `purgeDueTs` would be guessing.
 */

function fileFromDto(f: BusinessModelFileDto): StoredFile {
  return {
    guid: f.guid,
    filename: f.filename ?? '',
    contentType: f.contentType ?? '',
    sizeBytes: f.sizeBytes ?? 0,
    kind: f.kind ?? 'DOCUMENT',
    downloadUrl: f.downloadUrl ?? '',
    uploadedTs: f.uploadedTs ?? '',
  };
}

export function pendingDeletionFromDto(d: PendingFileDeletionDto): PendingFileDeletion {
  return {
    attachmentGuid: d.attachmentGuid,
    file: fileFromDto(d.file),
    // Never below 1: a row on the queue is at least the file itself, and 0 would
    // render as "0 versions will be destroyed", which is the opposite of true.
    versionCount: Math.max(1, d.versionCount ?? 1),
    totalSizeBytes: d.totalSizeBytes ?? 0,
    sourceTable: d.sourceTable ?? '',
    sourceDescription: d.sourceDescription ?? '',
    label: d.label ?? '',
    deletedBy: d.deletedBy ?? null,
    deletedTs: d.deletedTs ?? '',
    purgeDueTs: d.purgeDueTs ?? '',
    daysRemaining: Math.max(0, d.daysRemaining ?? 0),
  };
}

export function recoveredFileFromDto(d: RecoveredFileDto): RecoveredFile {
  return {
    recoveredFileGuid: d.recoveredFileGuid,
    file: fileFromDto(d.file),
    sourceTable: d.sourceTable ?? '',
    sourceDescription: d.sourceDescription ?? '',
    label: d.label ?? '',
    deletedBy: d.deletedBy ?? null,
    deletedTs: d.deletedTs ?? '',
    rescuedBy: d.rescuedBy ?? null,
    rescuedTs: d.rescuedTs ?? '',
    notes: d.notes ?? '',
  };
}
